/**
 * @fileoverview V2 Coverage Step — Insurance coverage organized by category.
 *
 * Three sections: First Party, Third Party, Health Insurance.
 * Each section has its own "not found" checkbox and list of coverage cards.
 * Same card-per-entry pattern as treatment providers.
 *
 * Audit-tracked: updates first_party_coverage, third_party_coverage,
 * health_insurance sections as found/not_found.
 */
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  useDeleteCoverageMutation,
  useGetCoveragesQuery,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import type { ImportedCoverageEntry } from '@/services/types/case-ingestion-assist-types';
import {
  BenefitType,
  CoverageCategory,
  CoverageStatus,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

import {
  type CoverageFormData,
  CoverageEntryForm,
  EMPTY_COVERAGE,
} from '../components/CoverageEntryForm';
import {
  FormCheckbox,
  FormGroup,
  FormSection,
} from '../components/form';
import { useIngestion } from '../state/IngestionProvider';

// ── Helpers ──────────────────────────────────────────────────────────────────

interface PendingEntry {
  id: string;
  data: CoverageFormData;
  isImported: boolean;
}

function matchPolicyType(raw: string | null, category: CoverageCategory): PolicyType | '' {
  if (category === CoverageCategory.FIRST_PARTY_HEALTH) return PolicyType.HEALTH;
  if (!raw) return '';
  const normalized = raw.toUpperCase().replace(/[\s-]+/g, '_');
  const match = Object.values(PolicyType).find((t) => t === normalized);
  return match ?? '';
}

function importedToFormData(imp: ImportedCoverageEntry, category: CoverageCategory): CoverageFormData {
  return {
    carrierName: imp.carrier_name || '',
    carrierId: null,
    divisionId: null,
    policyType: matchPolicyType(imp.policy_type, category),
    policyNumber: imp.policy_number || '',
    claimNumber: imp.claim_number || '',
    adjusterName: imp.adjuster_name || '',
    adjusterPhone: imp.adjuster_phone || '',
    adjusterEmail: imp.adjuster_email || '',
    adjusterFax: imp.adjuster_fax || '',
    namedInsured: imp.named_insured || '',
    benefitType: '',
    policyLimit: imp.policy_limit || '',
  };
}

// ── Coverage Category Section ────────────────────────────────────────────────

interface SavedCoverage {
  id: string;
  carrierName: string;
  carrierId: string | null;
  divisionId: string | null;
  policyType: string;
  policyNumber: string;
  claimNumber: string;
  namedInsured: string;
  adjusterName: string;
  adjusterPhone: string;
  adjusterEmail: string;
  adjusterFax: string;
  benefitType: string;
  policyLimit: string;
}

interface CategorySectionProps {
  title: string;
  category: CoverageCategory;
  matterId: string;
  notFound: boolean;
  onNotFoundChange: (checked: boolean) => void;
  pendingEntries: PendingEntry[];
  onAddEmpty: () => void;
  onRemovePending: (id: string) => void;
  onEntryAdded: (id: string) => void;
  savedCoverages: SavedCoverage[];
  onDeleteSaved: (coverageId: string) => void;
  onUpdateSaved: (coverageId: string, payload: Record<string, unknown>) => Promise<void>;
  isDeleting: boolean;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  title,
  category,
  matterId,
  notFound,
  onNotFoundChange,
  pendingEntries,
  onAddEmpty,
  onRemovePending,
  onEntryAdded,
  savedCoverages,
  onDeleteSaved,
  onUpdateSaved,
  isDeleting,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const hasAnything = savedCoverages.length > 0 || pendingEntries.length > 0;

  return (
    <FormSection title={title}>
      <FormGroup>
        {!hasAnything && (
          <FormCheckbox
            label="Not found"
            description={`No ${title.toLowerCase()} identified`}
            checked={notFound}
            onChange={(e) => onNotFoundChange((e.target as HTMLInputElement).checked)}
          />
        )}

        {!notFound && (
          <div className="space-y-3">
            {/* Pending entry forms */}
            {pendingEntries.map((entry, idx) => (
              <CoverageEntryForm
                key={entry.id}
                data={entry.data}
                isImported={entry.isImported}
                index={savedCoverages.length + idx}
                matterId={matterId}
                category={category}
                onAdded={() => onEntryAdded(entry.id)}
                onRemove={() => onRemovePending(entry.id)}
              />
            ))}

            {/* Saved coverages */}
            {savedCoverages.length > 0 && (
              <div className="space-y-2">
                {savedCoverages.map((cov) => {
                  if (editingId === cov.id) {
                    return (
                      <CoverageEntryForm
                        key={cov.id}
                        data={{
                          carrierName: cov.carrierName,
                          carrierId: cov.carrierId,
                          divisionId: cov.divisionId,
                          policyType: cov.policyType as PolicyType | '',
                          policyNumber: cov.policyNumber,
                          claimNumber: cov.claimNumber,
                          namedInsured: cov.namedInsured,
                          adjusterName: cov.adjusterName,
                          adjusterPhone: cov.adjusterPhone,
                          adjusterEmail: cov.adjusterEmail,
                          adjusterFax: cov.adjusterFax,
                          benefitType: cov.benefitType as BenefitType | '',
                          policyLimit: cov.policyLimit,
                        }}
                        isImported={false}
                        isEditing
                        index={savedCoverages.indexOf(cov)}
                        matterId={matterId}
                        category={category}
                        onAdded={() => setEditingId(null)}
                        onRemove={() => setEditingId(null)}
                        onUpdate={async (payload) => {
                          await onUpdateSaved(cov.id, payload);
                        }}
                      />
                    );
                  }

                  return (
                    <div
                      key={cov.id}
                      className="flex items-start justify-between gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">
                          {cov.carrierName || 'Unknown carrier'}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {[
                            cov.policyNumber && `Policy: ${cov.policyNumber}`,
                            cov.claimNumber && `Claim: ${cov.claimNumber}`,
                            cov.adjusterName && `Adjuster: ${cov.adjusterName}`,
                          ].filter(Boolean).join(' · ') || 'No details'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingId(cov.id)}
                          className="rounded p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteSaved(cov.id)}
                          disabled={isDeleting}
                          className="rounded p-1 text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add another */}
            <button
              type="button"
              onClick={onAddEmpty}
              className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
              <Plus className="h-3 w-3" />
              Add coverage
            </button>
          </div>
        )}
      </FormGroup>
    </FormSection>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

export const CoverageStep: React.FC = () => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const matterId = state.matterId!;

  // ── Data fetching ──────────────────────────────────────────────────────
  const { data: existingCoverages, refetch } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );
  const { data: auditRecord } = useGetCaseIngestionQuery(matterId);
  const [updateSections] = useUpdateCaseIngestionSectionsMutation();
  const [deleteCoverage, { isLoading: isDeleting }] = useDeleteCoverageMutation();
  const [updateCoverage] = useUpdateCoverageMutation();

  // ── State per category ─────────────────────────────────────────────────
  const [firstPartyNotFound, setFirstPartyNotFound] = useState(false);
  const [thirdPartyNotFound, setThirdPartyNotFound] = useState(false);
  const [healthNotFound, setHealthNotFound] = useState(false);

  const [firstPartyPending, setFirstPartyPending] = useState<PendingEntry[]>([]);
  const [thirdPartyPending, setThirdPartyPending] = useState<PendingEntry[]>([]);
  const [healthPending, setHealthPending] = useState<PendingEntry[]>([]);

  const hasInitialized = useRef(false);

  // ── Derived saved coverages by category ────────────────────────────────
  const activeCoverages = useMemo(
    () => (existingCoverages ?? []).filter((c) => c.status !== CoverageStatus.REJECTED),
    [existingCoverages],
  );

  const mapSaved = (cat: CoverageCategory): SavedCoverage[] =>
    activeCoverages
      .filter((c) => c.category === cat)
      .map((c) => {
        const adjuster = c.adjusters?.[0];
        const benefit = c.benefits?.[0];
        return {
          id: c.id,
          carrierName: c.carrier?.name || '',
          carrierId: c.carrier_v2_id || null,
          divisionId: c.carrier_division_id || null,
          policyType: c.policy_type || '',
          policyNumber: c.policy_identifiers?.find((p) => p.identifier_type === IdentifierType.POLICY_NUMBER)?.identifier_value || '',
          claimNumber: c.claims?.[0]?.claim_identifier || '',
          namedInsured: c.named_insured?.[0] || '',
          adjusterName: adjuster?.name || '',
          adjusterPhone: adjuster?.handles?.find((h) => h.handle_type === 'PHONE')?.value || '',
          adjusterEmail: adjuster?.handles?.find((h) => h.handle_type === 'EMAIL')?.value || '',
          adjusterFax: adjuster?.handles?.find((h) => h.handle_type === 'FAX')?.value || '',
          benefitType: benefit?.benefit_type || '',
          policyLimit: benefit?.limitations?.limit ? `$${benefit.limitations.limit.toLocaleString()}` : '',
        };
      });

  const savedFirstParty = useMemo(() => mapSaved(CoverageCategory.FIRST_PARTY), [activeCoverages]);
  const savedThirdParty = useMemo(() => mapSaved(CoverageCategory.THIRD_PARTY), [activeCoverages]);
  const savedHealth = useMemo(() => mapSaved(CoverageCategory.FIRST_PARTY_HEALTH), [activeCoverages]);

  // ── Initialize ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasInitialized.current) return;
    if (auditRecord === undefined || existingCoverages === undefined) return;
    hasInitialized.current = true;

    const sections = auditRecord?.sections;
    setFirstPartyNotFound(sections?.first_party_coverage === 'not_found');
    setThirdPartyNotFound(sections?.third_party_coverage === 'not_found');
    setHealthNotFound(sections?.health_insurance === 'not_found');

    // Create pending entries from import (only if no existing coverages in that category)
    const imp = importData?.payload?.coverage;
    if (!imp) return;

    // Only import into categories that have no existing coverages
    const hasSavedInCategory = (cat: CoverageCategory) =>
      activeCoverages.some((c) => c.category === cat);

    const createEntries = (
      imported: ImportedCoverageEntry[] | null | undefined,
      presence: string | undefined,
      category: CoverageCategory,
    ): PendingEntry[] => {
      if (presence !== 'present' || !imported?.length) return [];
      if (hasSavedInCategory(category)) return []; // Skip if already has saved data
      return imported.map((e, idx) => ({
        id: `import-${category}-${idx}`,
        data: importedToFormData(e, category),
        isImported: true,
      }));
    };

    setFirstPartyPending(
      createEntries(imp.first_party.value, imp.first_party.presence, CoverageCategory.FIRST_PARTY),
    );
    setThirdPartyPending(
      createEntries(imp.third_party.value, imp.third_party.presence, CoverageCategory.THIRD_PARTY),
    );
    setHealthPending(
      createEntries(imp.health_insurance.value, imp.health_insurance.presence, CoverageCategory.FIRST_PARTY_HEALTH),
    );
  }, [auditRecord, existingCoverages, importData, activeCoverages]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleUpdateSaved = async (coverageId: string, payload: Record<string, unknown>) => {
    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: payload as Record<string, unknown>,
      }).unwrap();
      toast.success('Coverage updated');
    } catch {
      toast.error('Failed to update coverage');
    }
  };

  const handleDeleteSaved = async (coverageId: string) => {
    try {
      await deleteCoverage({ matterId, coverageId }).unwrap();
    } catch {
      toast.error('Failed to delete coverage');
    }
  };

  const totalPending = firstPartyPending.length + thirdPartyPending.length + healthPending.length;

  // ── Save ───────────────────────────────────────────────────────────────
  const saveForm = useCallback(async (): Promise<boolean> => {
    if (totalPending > 0) {
      toast.error('Please add or remove all pending coverages before continuing');
      return false;
    }

    try {
      const updates: Record<string, string> = {};

      if (firstPartyNotFound) updates.first_party_coverage = 'not_found';
      else if (savedFirstParty.length > 0) updates.first_party_coverage = 'found';

      if (thirdPartyNotFound) updates.third_party_coverage = 'not_found';
      else if (savedThirdParty.length > 0) updates.third_party_coverage = 'found';

      if (healthNotFound) updates.health_insurance = 'not_found';
      else if (savedHealth.length > 0) updates.health_insurance = 'found';

      if (Object.keys(updates).length > 0) {
        await updateSections({
          matterId,
          data: { sections: updates },
        }).unwrap();
      }

      toast.success('Coverage saved');
      return true;
    } catch {
      toast.error('Failed to save coverage');
      return false;
    }
  }, [totalPending, firstPartyNotFound, thirdPartyNotFound, healthNotFound, savedFirstParty.length, savedThirdParty.length, savedHealth.length, matterId, updateSections]);

  useEffect(() => {
    registerSave(saveForm);
    return () => registerSave(null);
  }, [registerSave, saveForm]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await saveForm();
    if (success) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
    }
  };

  const hasPending = totalPending > 0;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <CategorySection
        title="First Party (1P) Coverage"
        category={CoverageCategory.FIRST_PARTY}
        matterId={matterId}
        notFound={firstPartyNotFound}
        onNotFoundChange={setFirstPartyNotFound}
        pendingEntries={firstPartyPending}
        onAddEmpty={() => setFirstPartyPending((p) => [...p, { id: `manual-1p-${Date.now()}`, data: { ...EMPTY_COVERAGE }, isImported: false }])}
        onRemovePending={(id) => setFirstPartyPending((p) => p.filter((e) => e.id !== id))}
        onEntryAdded={(id) => { setFirstPartyPending((p) => p.filter((e) => e.id !== id)); refetch(); }}
        savedCoverages={savedFirstParty}
        onDeleteSaved={handleDeleteSaved}
        onUpdateSaved={handleUpdateSaved}
        isDeleting={isDeleting}
      />

      <CategorySection
        title="Third Party (3P) Coverage"
        category={CoverageCategory.THIRD_PARTY}
        matterId={matterId}
        notFound={thirdPartyNotFound}
        onNotFoundChange={setThirdPartyNotFound}
        pendingEntries={thirdPartyPending}
        onAddEmpty={() => setThirdPartyPending((p) => [...p, { id: `manual-3p-${Date.now()}`, data: { ...EMPTY_COVERAGE }, isImported: false }])}
        onRemovePending={(id) => setThirdPartyPending((p) => p.filter((e) => e.id !== id))}
        onEntryAdded={(id) => { setThirdPartyPending((p) => p.filter((e) => e.id !== id)); refetch(); }}
        savedCoverages={savedThirdParty}
        onDeleteSaved={handleDeleteSaved}
        onUpdateSaved={handleUpdateSaved}
        isDeleting={isDeleting}
      />

      <CategorySection
        title="Health Insurance"
        category={CoverageCategory.FIRST_PARTY_HEALTH}
        matterId={matterId}
        notFound={healthNotFound}
        onNotFoundChange={setHealthNotFound}
        pendingEntries={healthPending}
        onAddEmpty={() => setHealthPending((p) => [...p, { id: `manual-health-${Date.now()}`, data: { ...EMPTY_COVERAGE, policyType: PolicyType.HEALTH }, isImported: false }])}
        onRemovePending={(id) => setHealthPending((p) => p.filter((e) => e.id !== id))}
        onEntryAdded={(id) => { setHealthPending((p) => p.filter((e) => e.id !== id)); refetch(); }}
        savedCoverages={savedHealth}
        onDeleteSaved={handleDeleteSaved}
        onUpdateSaved={handleUpdateSaved}
        isDeleting={isDeleting}
      />

      {/* Submit */}
      <button
        type="submit"
        disabled={isDeleting || hasPending}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transition-colors duration-150'
        }
      >
        {hasPending ? 'Add or remove pending coverages first' : 'Save & Continue'}
      </button>
    </form>
  );
};
